import { ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isBefore, startOfDay } from 'date-fns';
import { isDemoModeActive } from '@/demo/demoMode';
import { PageBottomPortalProvider } from '@/enterprise/components/PageBottomPortal/PageBottomPortalContext';
import { useAlertArtifacts } from '@/enterprise/data/useArtifacts';
import { useShowPrivacyPolicyBanner } from '@/enterprise/data/useTerms';
import HolidayDelayBanner from '@/enterprise/features/homepage/HolidayDelayBanner';
import OperationalAlert, { OPERATIONAL_ALERT_PLACEMENT } from '@/enterprise/features/homepage/OperationalAlert';
import PrivacyPolicyBanner from '@/enterprise/features/homepage/PrivacyPolicyBanner';
import useFeature from '@/lib/features';
import { isImpersonationSession } from '@/utils/token';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import DemoBanner from '../../demo/components/DemoBanner';
import useCurrencies from '../data/useCurrencies';
import Footer from './Footer';
import { ImpersonationBanner } from './ImpersonationBanner';
import ReadOnlyBanner from './ReadOnlyBanner';
import SideNavigation from './SideNavigation/Navigation';
import SideNavigationMobile from './SideNavigation/NavigationMobile';
import SideNavigationTop from './SideNavigation/NavigationTop';

/** Module scope so `useAlertArtifacts` can memoize its selection across renders. */
const OPERATIONAL_ALERT_PLACEMENTS = [OPERATIONAL_ALERT_PLACEMENT];

/** Measured height for layout math inside this component. */
const useElementHeight = (): readonly [RefObject<HTMLDivElement | null>, number] => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;

    if (!el) {
      return;
    }

    const publish = (): void => {
      setHeight(el.offsetHeight);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return [ref, height] as const;
};

/**
 * Publishes a measured height on the document root.
 * Reason: portaled modals and drawers sit outside this tree, so they can only read the header
 * height from a document-level variable. Measuring it stays correct if a banner wraps.
 */
const useHeightCssVar = (cssVarName: string): RefObject<HTMLDivElement | null> => {
  const [ref, height] = useElementHeight();

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(cssVarName, `${height}px`);

    return () => {
      document.documentElement.style.removeProperty(cssVarName);
    };
  }, [cssVarName, height]);

  return ref;
};

const AppLayout = ({ children }: { children: ReactNode }) => {
  const readOnlyUser = useReadOnlyUser();
  const isImpersonation = isImpersonationSession();
  const { showPrivacyPolicyBanner } = useShowPrivacyPolicyBanner();
  const pageBottomPortalRef = useRef<HTMLDivElement>(null);
  // The sidebar, the inset banners, and portaled modals/drawers all need to start below this bar.
  const appHeaderRef = useHeightCssVar('--enterprise-header-height');
  // SideNavigationTop sticks below the header plus this bar. Local state: only this component reads it.
  const [insetBannerRef, insetBannerHeight] = useElementHeight();
  const [enablePrivacyPolicyBanner] = useFeature('enterprise-ui_enablePrivacyPolicyBanner');
  const [useArtifactsEnabled] = useFeature('enterprise-ui_useMarketingArtifacts');
  const currencies = useCurrencies();
  const { i18n } = useTranslation();
  const { data: selectedAlert, isLoading: isAlertArtifactsLoading } = useAlertArtifacts(OPERATIONAL_ALERT_PLACEMENTS);

  const hasEligibleCurrency = currencies.some((currency) => ['USD', 'GBP', 'EUR'].includes(currency));
  const isEnglish = i18n.language.startsWith('en');
  // startOfDay(new Date()) is today at local midnight. new Date(2026, 6, 4) is July 4 at local midnight, month is 0-indexed.
  // Window covers today through Independence Day observed (Jul 3).
  const isBeforeJul4th = isBefore(startOfDay(new Date()), startOfDay(new Date(2026, 6, 4)));
  const holidayConditionsMet = hasEligibleCurrency && isEnglish && isBeforeJul4th;

  const [holidayDelayBannerDismissed, setHolidayDelayBannerDismissed] = useState(false);
  const [operationalAlertDismissed, setOperationalAlertDismissed] = useState(false);

  const showOperationalAlert = useArtifactsEnabled && !!selectedAlert && !operationalAlertDismissed;

  // Flag off: unchanged holiday path. Flag on: wait for Alert query; prefer SEA alert; else holiday fallback.
  const showHolidayDelayBanner =
    !showOperationalAlert &&
    holidayConditionsMet &&
    !holidayDelayBannerDismissed &&
    (!useArtifactsEnabled || !isAlertArtifactsLoading);

  const showPPBanner = enablePrivacyPolicyBanner && showPrivacyPolicyBanner && !isImpersonation && !readOnlyUser;
  const isDemoActive = isDemoModeActive();

  const operationalAlertDismissible = selectedAlert?.display?.dismissible !== false;

  return (
    <div className="flex min-h-screen w-full flex-col bg-white">
      {/* Full-width so it spans over the sidebar too, not just the content column. */}
      <div ref={appHeaderRef} className="sticky top-0 z-10">
        {isDemoActive && <DemoBanner />}
        {isImpersonation && <ImpersonationBanner />}
        {readOnlyUser && <ReadOnlyBanner isImpersonation={isImpersonation} />}
        <SideNavigationMobile />
      </div>
      <div className="flex flex-1">
        {/*
         * A normal flex sibling now, not `fixed`: it can't render over the header above it, and the
         * content column no longer needs a padding-left hack to match its width.
         */}
        <SideNavigation
          className="sticky hidden shrink-0 md:block"
          style={{ top: 'var(--enterprise-header-height)', height: 'calc(100vh - var(--enterprise-header-height))' }}
        />
        <div className="flex min-w-0 flex-1 flex-col bg-canvas">
          <PageBottomPortalProvider containerRef={pageBottomPortalRef}>
            <div className="flex min-h-full flex-col">
              {/* Inset banners: start after the sidebar, span to the browser's right edge. */}
              <div ref={insetBannerRef} className="sticky z-10" style={{ top: 'var(--enterprise-header-height)' }}>
                {showPPBanner && <PrivacyPolicyBanner />}
                {showOperationalAlert && selectedAlert && (
                  <OperationalAlert
                    body={selectedAlert.content.body}
                    bodyFormat={selectedAlert.content.bodyFormat}
                    dismissible={operationalAlertDismissible}
                    tracking={selectedAlert.tracking}
                    onDismiss={() => setOperationalAlertDismissed(true)}
                  />
                )}
                {showHolidayDelayBanner && (
                  <HolidayDelayBanner onDismiss={() => setHolidayDelayBannerDismissed(true)} />
                )}
              </div>
              <div
                className="sticky z-[9]"
                style={{
                  top: `calc(var(--enterprise-header-height) + ${insetBannerHeight}px)`,
                }}
              >
                <SideNavigationTop />
              </div>
              <div className="grow">
                <div className="mx-auto max-w-screen-xl">
                  <div className="px-4 py-10 md:px-6">{children}</div>
                  <Footer />
                </div>
              </div>
              <div ref={pageBottomPortalRef} className="pointer-events-none sticky bottom-0 z-[9]" />
            </div>
          </PageBottomPortalProvider>
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
